import React from "react";
import FinexLogo from "../../components/FinexLogo";

/**
 * Printable / downloadable Sales Invoice document.
 *
 * Shares the visual language of the inventory Purchase Order / Stock Transfer
 * documents: teal accent letterhead, party blocks, refined line table and a
 * min-height flex column that fills an A4 page (bank details + footer pinned to
 * the bottom). Self-contained inline styles so it renders identically through
 * window.print() and html2pdf.
 *
 * Bilingual fixed labels (Albanian / English); fiscal identifiers (NUI / TVSH)
 * and bank details are preserved as they are legally required on the invoice.
 */

// Palette — restrained, one teal accent (the Finex brand colour).
const ACCENT = "#0F7679";
const INK = "#1F2937";
const MUTED = "#6B7280";
const LINE = "#E6E8EB";

const formatDateDefault = (dateString: any) => {
  if (!dateString) return "–";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const join = (parts: Array<string | null | undefined>) =>
  parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

export interface SalesPrintViewProps {
  invoiceNumber?: string;
  companyDetail?: {
    companyName?: string;
    companyLogo?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: { name?: string } | null;
    emailAddress?: string;
    phoneNumber?: string | null;
    companyNumber?: string;
    fiscalNumber?: string | null;
  } | null;
  clientDetail?: {
    companyName?: string;
    addressLine1?: string | null;
    addressLine2?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: { name?: string } | null;
    emailAddress?: string | null;
    phoneNumber?: string | null;
    companyNumber?: string;
    taxCode?: string | null;
  } | null;
  issuedOn?: string | Date | null;
  /** Payment term, already translated by the caller (e.g. "30 ditë"). */
  paymentTerm?: string | null;
  locationName?: string;
  rows?: Array<{
    sequenceNo?: number;
    sku?: string;
    itemName?: string;
    quantity?: number;
    price?: number | string;
    tax?: number | string;
    discount?: number | string;
    amount?: number | string;
  }>;
  companyBankDetails?: Array<{
    bankName?: string;
    accountNo?: string | null;
    IBAN?: string | null;
    SWIFT?: string | null;
  }>;
  formatDate?: (date: any) => string;
}

export const SalesPrintView: React.FC<SalesPrintViewProps> = ({
  invoiceNumber = "",
  companyDetail,
  clientDetail,
  issuedOn,
  paymentTerm,
  locationName = "",
  rows = [],
  companyBankDetails = [],
  formatDate = formatDateDefault,
}) => {
  const detailRows = Array.isArray(rows)
    ? rows.filter((r) => r?.itemName != null && r?.itemName !== "")
    : [];

  const money = (n: number) =>
    `${new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0)} €`;
  const qty = (n: any) =>
    new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(Number(n) || 0);

  // Document totals. `amount` is the gross line total, so the net subtotal is
  // the gross minus VAT; this mirrors the on-screen invoice summary exactly.
  const totalAmount = detailRows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
  const totalTax = detailRows.reduce((acc, r) => acc + (Number(r.tax) || 0), 0);
  const totalDiscount = detailRows.reduce((acc, r) => acc + (Number(r.discount) || 0), 0);
  const subtotal = totalAmount - totalTax;
  const invoiceRef = invoiceNumber ? invoiceNumber : "–";
  // "Afati / Due by": the payment-term name only (e.g. "30 ditë"), already
  // translated by the caller through the app language dictionary.
  const termLabel = paymentTerm ? String(paymentTerm).trim() : "";

  const metaLabel: React.CSSProperties = {
    fontSize: "10px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: MUTED,
    fontWeight: 600,
  };
  const th: React.CSSProperties = { ...metaLabel, padding: "10px 7px", textAlign: "left" };
  const thRight: React.CSSProperties = { ...th, textAlign: "right" };
  const td: React.CSSProperties = {
    padding: "10px 7px",
    color: INK,
    borderBottom: `1px solid ${LINE}`,
    verticalAlign: "top",
  };
  const tdRight: React.CSSProperties = {
    ...td,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  };

  const companyAddr = join([
    companyDetail?.addressLine1,
    companyDetail?.addressLine2,
    join([companyDetail?.postalCode, companyDetail?.city]),
    companyDetail?.country?.name,
  ]);
  const clientAddr = join([
    clientDetail?.addressLine1,
    clientDetail?.addressLine2,
    join([clientDetail?.postalCode, clientDetail?.city]),
    clientDetail?.country?.name,
  ]);
  const companyContact = join([companyDetail?.emailAddress, companyDetail?.phoneNumber]);
  const clientContact = join([clientDetail?.emailAddress, clientDetail?.phoneNumber]);

  return (
    <div
      className="sales-print-view"
      style={{
        width: "760px",
        minHeight: "1040px",
        boxSizing: "border-box",
        padding: "44px 44px 32px",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Inter, Helvetica, Arial, sans-serif",
        fontSize: "13px",
        lineHeight: 1.45,
        color: INK,
        background: "#fff",
      }}
    >
      {/* Letterhead: issuer (company) left + document meta right */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          paddingBottom: "20px",
          borderBottom: `2px solid ${ACCENT}`,
        }}
      >
        <div style={{ maxWidth: "58%" }}>
          {companyDetail?.companyLogo && (
            <img
              src={companyDetail.companyLogo}
              alt=""
              style={{ maxWidth: "150px", maxHeight: "56px", marginBottom: "10px", display: "block" }}
            />
          )}
          <div style={{ fontWeight: 700, fontSize: "20px", color: INK }}>
            {companyDetail?.companyName ?? "–"}
          </div>
          {companyAddr && <div style={{ color: MUTED, fontSize: "12px" }}>{companyAddr}</div>}
          {companyContact && <div style={{ color: MUTED, fontSize: "12px" }}>{companyContact}</div>}
          <div style={{ color: INK, fontSize: "12px", marginTop: "2px" }}>
            NUI {companyDetail?.companyNumber ?? "–"} · TVSH {companyDetail?.fiscalNumber ?? "–"}
          </div>
          {locationName && (
            <div style={{ color: MUTED, fontSize: "12px" }}>Njësia {locationName}</div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{ fontWeight: 700, fontSize: "15px", color: ACCENT, letterSpacing: "0.04em" }}
          >
            FATURË / INVOICE
          </div>
          <div style={{ marginTop: "8px", fontSize: "12px" }}>
            <div style={{ color: MUTED }}>
              Nr.: <span style={{ color: INK, fontWeight: 700 }}>{invoiceRef}</span>
            </div>
            <div style={{ color: MUTED, marginTop: "2px" }}>
              Data: <span style={{ color: INK, fontWeight: 600 }}>{issuedOn ? formatDate(issuedOn) : "–"}</span>
            </div>
            {termLabel && (
              <div style={{ color: MUTED, marginTop: "2px" }}>
                Afati / Due by: <span style={{ color: INK, fontWeight: 600 }}>{termLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Buyer */}
      <div style={{ marginTop: "26px", marginBottom: "24px" }}>
        <div style={{ ...metaLabel, marginBottom: "6px" }}>Blerës / Buyer</div>
        <div style={{ fontWeight: 700, fontSize: "14px", color: INK }}>
          {clientDetail?.companyName ?? "–"}
        </div>
        {clientAddr && <div style={{ color: MUTED, fontSize: "12px" }}>{clientAddr}</div>}
        {clientContact && <div style={{ color: MUTED, fontSize: "12px" }}>{clientContact}</div>}
        <div style={{ color: INK, fontSize: "12px", marginTop: "2px" }}>
          NUI {clientDetail?.companyNumber ?? "–"} · TVSH {clientDetail?.taxCode ?? "–"}
        </div>
      </div>

      {/* Line table */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${LINE}` }}>
        <thead>
          <tr style={{ backgroundColor: "#F7F8F9", borderBottom: `1px solid ${LINE}` }}>
            <th style={{ ...th, width: "26px" }}>Nr.</th>
            <th style={{ ...th, width: "70px" }}>Kodi</th>
            <th style={th}>Emërtimi</th>
            <th style={{ ...thRight, width: "42px" }}>Sasia</th>
            <th style={{ ...thRight, width: "74px" }}>Çmimi</th>
            <th style={{ ...thRight, width: "62px" }}>TVSH</th>
            <th style={{ ...thRight, width: "62px" }}>Rabati</th>
            <th style={{ ...thRight, width: "82px" }}>Totali</th>
          </tr>
        </thead>
        <tbody>
          {detailRows.length === 0 ? (
            <tr>
              <td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={8}>
                Nuk ka artikuj në këtë faturë.
              </td>
            </tr>
          ) : (
            detailRows.map((row, idx) => (
              <tr
                key={row.sequenceNo ?? idx}
                style={{ backgroundColor: idx % 2 === 1 ? "#FBFBFC" : "#fff" }}
              >
                <td style={{ ...td, color: MUTED }}>{row.sequenceNo ?? idx + 1}</td>
                <td style={td}>{row.sku ?? "–"}</td>
                <td style={{ ...td, fontWeight: 600 }}>{row.itemName ?? "–"}</td>
                <td style={tdRight}>{row.quantity != null ? qty(row.quantity) : "–"}</td>
                <td style={tdRight}>{row.price != null ? money(Number(row.price)) : "–"}</td>
                <td style={tdRight}>{row.tax != null ? money(Number(row.tax)) : "–"}</td>
                <td style={tdRight}>{row.discount != null ? money(Number(row.discount)) : "–"}</td>
                <td style={{ ...tdRight, fontWeight: 600 }}>
                  {row.amount != null ? money(Number(row.amount)) : "–"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
        <table
          style={{ borderCollapse: "collapse", minWidth: "280px", fontVariantNumeric: "tabular-nums" }}
        >
          <tbody>
            <tr>
              <td style={{ padding: "5px 0", color: MUTED }}>Nën-total</td>
              <td style={{ padding: "5px 0", textAlign: "right", color: INK }}>{money(subtotal)}</td>
            </tr>
            {totalDiscount > 0 && (
              <tr>
                <td style={{ padding: "5px 0", color: MUTED }}>Zbritje</td>
                <td style={{ padding: "5px 0", textAlign: "right", color: INK }}>
                  {money(totalDiscount)}
                </td>
              </tr>
            )}
            <tr>
              <td style={{ padding: "5px 0", color: MUTED }}>TVSH</td>
              <td style={{ padding: "5px 0", textAlign: "right", color: INK }}>{money(totalTax)}</td>
            </tr>
            <tr>
              <td
                style={{
                  padding: "10px 0 0",
                  borderTop: `2px solid ${ACCENT}`,
                  fontWeight: 700,
                  fontSize: "15px",
                }}
              >
                Totali
              </td>
              <td
                style={{
                  padding: "10px 0 0",
                  borderTop: `2px solid ${ACCENT}`,
                  textAlign: "right",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: ACCENT,
                }}
              >
                {money(totalAmount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Spacer — pushes bank details + footer to the bottom of the page */}
      <div style={{ flex: 1, minHeight: "32px" }} />

      {/* Bank details */}
      {companyBankDetails.length > 0 && (
        <div style={{ paddingTop: "16px", borderTop: `1px solid ${LINE}` }}>
          <div style={{ ...metaLabel, marginBottom: "6px" }}>Të dhënat bankare / Bank details</div>
          {companyBankDetails.map((bank, idx) => (
            <div key={idx} style={{ fontSize: "12px", color: INK, marginBottom: "3px" }}>
              <span style={{ fontWeight: 600 }}>{bank.bankName}</span>
              {bank.accountNo ? ` · ${bank.accountNo}` : ""}
              <span style={{ color: MUTED }}>
                {bank.IBAN ? ` · IBAN ${bank.IBAN}` : ""}
                {bank.SWIFT ? ` · SWIFT ${bank.SWIFT}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "24px",
          paddingTop: "12px",
          borderTop: `1px solid ${LINE}`,
          fontSize: "10px",
          color: MUTED,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FinexLogo height={15} />
          <span>www.meetfinex.com</span>
        </span>
        <span>1 / 1</span>
      </div>
    </div>
  );
};

export default SalesPrintView;
